import { InfoTooltipComponent } from "@zenith/components";
import { formatCurrency } from "@zenith/utils";
import { memo, useState } from "react";
import _ from "lodash";
import { ArrowDown, ArrowUp } from "iconsax-react";
import BidirectionalArrow from "@zenith/assets/icons/components/BidirectionalArrow";

/* eslint-disable-next-line */
export interface SellersCardProps {
  itemName: string;
  unitPrice: number;
  quantity: number;
  pcs: number;
  dataFilter: string;
  salesDescription: string;
  revenueDescription: string;
  revenue: number;
  periodicRevenue: number;
  title: string;
}

const SellersCard = memo(function SellersCard(props: SellersCardProps) {
  const {
    itemName,
    unitPrice,
    quantity,
    dataFilter,
    pcs,
    revenueDescription,
    salesDescription,
    revenue,
    periodicRevenue,
    title,
  } = props;

  const [openSalesTooltip, setOpenSalesTooltip] = useState<boolean>(false);
  const [openRevenueTooltip, setOpenRevenueTooltip] = useState<boolean>(false);

  const salesTooltipId = _.kebabCase("quantity-sold");
  const revenueTooltipId = _.kebabCase("generated-revenue");

  const pieces = Number(pcs);
  const generatedRevenue = Number(periodicRevenue);

  return (
    <div className="mt-5">
      <h2 className="mb-3 text-sm font-semibold text-neutral-600">{title}</h2>

      <div className="flex h-full min-h-209 flex-col justify-between rounded-lg border border-neutral-50 p-6">
        <div>
          <h3 className="mb-1 text-2xl font-medium text-black">{itemName}</h3>
          <p className="text-xs font-light text-neutral-300">
            Unit Price -{" "}
            <span className="text-neutral-600">
              {formatCurrency(unitPrice)}
            </span>
          </p>
        </div>

        <div className="mt-auto block justify-between md:flex">
          {/* Sales Info */}
          <div className=" grid gap-3">
            <div className="flex gap-2">
              <p className="text-sm text-neutral-600">Quantity Sold</p>
              <div className="h-3 w-4">
                <InfoTooltipComponent
                  handleTooltipClose={() => setOpenSalesTooltip(false)}
                  handleTooltipOpen={() => setOpenSalesTooltip(true)}
                  open={openSalesTooltip}
                  content={
                    <p className="max-w-[20ch] break-words p-1">
                      {salesDescription}
                    </p>
                  }
                  tooltipId={salesTooltipId}
                  iconColor="#626262"
                />
              </div>
            </div>
            <p className="text-2xl font-medium">{Math.abs(quantity || 0)}</p>
            <div className="flex gap-1">
              <div
                className={`flex items-center justify-start gap-1 ${
                  pieces < 0
                    ? "text-red-600"
                    : pieces === 0
                    ? "text-neutral-200"
                    : "text-green-600"
                }`}
              >
                {pieces < 0 ? (
                  <ArrowDown className="h-4 w-4" />
                ) : pieces === 0 ? (
                  <BidirectionalArrow />
                ) : (
                  <ArrowUp className="h-4 w-4" />
                )}

                <span className="text-xs font-normal">
                  {Math.abs(pcs)?.toLocaleString()}pcs
                </span>
              </div>
              <p className="text-neutral-60 text-xs">vs {dataFilter}</p>
            </div>
          </div>

          <hr className="mx-3 hidden h-full w-0 border-0.5 border-neutral-50 md:inline-block" />

          {/* Revenue Info */}
          <div className="mt-4 grid gap-3 pr-10 md:mt-0">
            <div className="flex gap-2">
              <p className="text-sm text-neutral-600">Generated Revenue</p>
              <div className="h-3 w-4">
                <InfoTooltipComponent
                  handleTooltipClose={() => setOpenRevenueTooltip(false)}
                  handleTooltipOpen={() => setOpenRevenueTooltip(true)}
                  open={openRevenueTooltip}
                  content={
                    <p className="max-w-[20ch] break-words p-1">
                      {revenueDescription}
                    </p>
                  }
                  tooltipId={revenueTooltipId}
                  iconColor="#626262"
                  nowrap
                />
              </div>
            </div>
            <p className="text-2xl font-medium">
              {formatCurrency(Math.abs(revenue))}
            </p>
            <div className="flex gap-1">
              <div
                className={`flex items-center justify-start gap-1 ${
                  generatedRevenue < 0
                    ? "text-red-600"
                    : generatedRevenue === 0
                    ? "text-neutral-200"
                    : "text-green-600"
                }`}
              >
                {generatedRevenue < 0 ? (
                  <ArrowDown className="h-4 w-4" />
                ) : generatedRevenue === 0 ? (
                  <BidirectionalArrow />
                ) : (
                  <ArrowUp className="h-4 w-4" />
                )}

                <span className="text-xs font-normal">
                  {formatCurrency(Math.abs(periodicRevenue))}
                </span>
              </div>
              <p className="text-neutral-60 text-xs">vs {dataFilter}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default SellersCard;
